/**
 * The Files page's list-level helpers: item keys, the tab set, drag-to-folder
 * wiring and the selection-key split. Moved out of `routes/files.tsx`
 * (FRONT-2434) so the page file holds the page and the Files tab its own
 * module; `routes/files.tsx` re-exports the ones its tests import.
 */

import { useFeatureFlag, KNOWLEDGE_SERVICE_FLAG, SKETCHES_FLAG, SLIDE_DECKS_FLAG } from '~/hooks/queries/use-feature-flags';
import type { UnifiedFileItem } from '~/hooks/queries/use-files-unified';
import type { FilesOverviewItem } from '~/hooks/queries/use-files-overview';
import type { FolderMembershipMapItem } from '~/hooks/queries/use-folders';
import type { PlanSource } from '~/hooks/mutations/use-plan-mutations';
import {
  isDesktopFileDrag,
  FOLDER_DRAG_MIME,
  resolveDragCarryKeys,
  buildDragGhostModel,
  mountDragGhost,
  type DragGhostSource,
  type DragGhostModel,
  type DragImageTransferLike,
} from '~/lib/file-drag';

/**
 * A file's own page — what a Files row's link points at, so Cmd/Ctrl-click
 * or a middle click opens it in a new tab (FRONT-2434). The routes are the
 * page's deep links: `/files/<id>` for an upload, `/files/doc/<filename>`
 * for a document.
 */
export function fileItemHref(item: UnifiedFileItem | FilesOverviewItem): string {
  return item.source === 'upload' ? `/files/${encodeURIComponent(item.id)}` : `/files/doc/${encodeURIComponent(item.filename)}`;
}

export function fileItemKey(item: UnifiedFileItem | FilesOverviewItem): string {
  return item.source === 'upload' ? `upload:${item.id}` : `doc:${item.filename}`;
}

/**
 * FRONT-1122: the "Create project" overflow-menu source for a Files card —
 * an upload's raw id (NOT the zen-modal's prefixed `file-<id>` form used
 * elsewhere on this page) for uploads, the filename for documents. Exported
 * so the exact wire shape sent to the dialog is unit-tested directly (no
 * click harness in this repo's frontend tests — see files.split.test.ts).
 */
export function planSourceForFileItem(item: UnifiedFileItem): { source: PlanSource; label: string } {
  return item.source === 'upload'
    ? { source: { kind: 'file', fileId: item.id }, label: item.name }
    : { source: { kind: 'document', filename: item.filename }, label: item.title };
}

/**
 * FRONT-1549: the Files tab strip's full set of possible tabs — 'bases' and
 * 'sketches' are each present only while their own flag is on (see
 * `FileList`'s `availableTabs`). Widened from the original `'open' |
 * 'trash'` so `shouldShowCreatePlanMenu`/`isFileCardDraggable` below take
 * the real tab type instead of being cast at every call site.
 */
export type FileListTab = 'open' | 'trash' | 'bases' | 'sketches' | 'slides' | 'okf';

/**
 * The tab strip's visible labels. A `Record` rather than a conditional chain
 * so adding a tab to `FileListTab` is a type error until it gets a label —
 * the chain it replaced silently fell through to "Recycle bin" instead
 * (PR #1064 review).
 */
export const TAB_LABELS: Record<FileListTab, string> = {
  open: 'Files',
  bases: 'Knowledge bases',
  sketches: 'Sketches',
  slides: 'Slides',
  okf: 'OKF',
  trash: 'Recycle bin',
};

/**
 * FRONT-1122 review fix: whether a card's "Create project" kebab should
 * render — never on a recycle-bin row (there is nothing to draft a project
 * FROM in the bin), and only while the flag is on. This is the actual gate
 * the Files tab's row actions call (`files-tab.tsx`, the More menu's
 * "Create project"), not a parallel copy — wiring it in as the one
 * source of truth means a regression that drops either half of the check
 * (moving the menu out of the open-tab branch, or dropping the flag check)
 * is caught by `shouldShowCreatePlanMenu`'s own unit coverage even though
 * this repo's frontend tests have no click harness to switch tabs and see it
 * visually (see files.split.test.ts). Never on the 'bases' or 'sketches'
 * tabs either — neither renders a file card to hang the kebab off.
 */
export function shouldShowCreatePlanMenu(tab: FileListTab, createPlanEnabled: boolean): boolean {
  return tab === 'open' && createPlanEnabled;
}

/**
 * FRONT-1549 review fix: resolves the `?tab=` URL param against the tabs
 * actually available right now — the same "no click harness" reasoning as
 * `shouldShowCreatePlanMenu`/`isFileCardDraggable` (this repo's frontend
 * tests can't mount `FileList` and switch tabs, so the gate has to be a pure
 * function `FileList` calls, not inline logic). An absent, unknown, or
 * currently-unavailable value (e.g. `?tab=sketches` with the flag off)
 * falls back to `'open'`.
 */
export function resolveFileListTab(
  param: string | null,
  availableTabs: readonly FileListTab[],
): FileListTab {
  return param && (availableTabs as readonly string[]).includes(param)
    ? (param as FileListTab)
    : 'open';
}

/**
 * FRONT-1605: which tabs render the shared file/bin surface — the search
 * box, the upload dropzone, the card grid and its empty states. Every other
 * tab ('bases', 'sketches', 'okf') brings its own body instead, and would
 * otherwise inherit a search box that ignores what you type and an empty
 * state that gives advice about the wrong thing. A pure function rather than
 * a chain of `tab !== …` checks repeated at each guard: the two guards drifted
 * apart once already, and this repo's frontend tests have no click harness to
 * catch that on screen (same reasoning as `shouldShowCreatePlanMenu`).
 */
export function showsFileGrid(tab: FileListTab): boolean {
  return tab === 'open' || tab === 'trash';
}

/**
 * FRONT-2488: which tabs get the page header's file actions (Export, Import,
 * New file). Only the Files tab: every other tab either holds its own kind of
 * content (knowledge bases, sketches, slides, the OKF settings) or discarded
 * material (the recycle bin), and a "New file" above them creates nothing the
 * tab shows.
 */
export function showsFileHeaderActions(tab: FileListTab): boolean {
  return tab === 'open';
}

/**
 * FRONT-1606: the tab set and the resolved active tab used to be computed
 * TWICE — once inside `FileList` (to drive the tab strip) and once again, by
 * hand, in `Component()` above (just to decide whether to show the
 * Export/Import buttons) — the same two feature-flag reads and the same
 * `?tab=` resolution, kept in sync only by both authors remembering to touch
 * both copies. A tab added to one and not the other would make the strip and
 * the header buttons silently disagree about what's showing. This hook is
 * the one place both call now, so that can no longer happen by construction.
 *
 * FRONT-1460: `KNOWLEDGE_SERVICE_FLAG` defaults `false` (fail-closed) — the
 * knowledge-bases route 404s while the flag is off, so an ungated query
 * would be a guaranteed failed request for a workspace that simply doesn't
 * have this.
 *
 * FRONT-1549: `SKETCHES_FLAG` defaults `true` (fail-open), unlike the flag
 * above — the `/api/sketches` routes answer 200 regardless of the flag
 * (`src/sketches/routes.ts`), and the sidebar entry this tab replaced was
 * fail-open too (`mission-control-sidebar.tsx`, `command-palette.tsx` both
 * pass `true`). Fail-closed here would make an already-created sketch
 * briefly (while flags load) or permanently (if `/api/feature-flags` errors,
 * or the flag isn't created yet) unreachable.
 */
export function useFileListTabState(searchParams: URLSearchParams): {
  tab: FileListTab;
  availableTabs: readonly FileListTab[];
  knowledgeEnabled: boolean;
} {
  const knowledgeEnabled = useFeatureFlag(KNOWLEDGE_SERVICE_FLAG, false);
  const sketchesEnabled = useFeatureFlag(SKETCHES_FLAG, true);
  // FRONT-1602: the Slides tab. Fail-CLOSED (default `false`), unlike
  // SKETCHES_FLAG above: decks are new, so there is no already-created deck
  // that a briefly-absent tab would strand, and the deck tools themselves
  // fail closed on the same flag — an open tab over closed tools would show
  // an empty gallery a user cannot fill.
  const decksEnabled = useFeatureFlag(SLIDE_DECKS_FLAG, false);
  const availableTabs: readonly FileListTab[] = [
    'open',
    ...(knowledgeEnabled ? (['bases'] as const) : []),
    ...(sketchesEnabled ? (['sketches'] as const) : []),
    ...(decksEnabled ? (['slides'] as const) : []),
    // FRONT-1605: the OKF overview sits immediately before the recycle bin —
    // ungated, like the panel it replaces, because it reads the same
    // always-available catalogue every workspace already has.
    'okf',
    'trash',
  ];
  const tab = resolveFileListTab(searchParams.get('tab'), availableTabs);
  return { tab, availableTabs, knowledgeEnabled };
}

/**
 * Split a set of selection keys back into file ids and document filenames.
 * Exported so the round-trip (a bulk trash → the matching bulk Undo restores
 * exactly the same uploads + documents) can be unit-guarded — FRONT-1019.
 */
export function splitFileKeys(keys: string[]): { fileIds: string[]; docNames: string[] } {
  const fileIds: string[] = [];
  const docNames: string[] = [];
  for (const k of keys) {
    if (k.startsWith('upload:')) fileIds.push(k.slice('upload:'.length));
    else if (k.startsWith('doc:')) docNames.push(k.slice('doc:'.length));
  }
  return { fileIds, docNames };
}

/**
 * FRONT-776 code-review round 3, finding 6: a search runs over the WHOLE
 * library, so a filed file can appear in results — but it used to render
 * IDENTICALLY to an unfiled one, even though the membership map already
 * carries the folder name for every filed ref. This builds a `fileItemKey`
 * → folder-name lookup once per membership fetch so a card can show "In
 * <folder>" instead of looking indistinguishable from an unfiled file.
 */
export function folderNameByFileKey(membership: FolderMembershipMapItem[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const m of membership) {
    map.set(m.source === 'upload' ? `upload:${m.ref}` : `doc:${m.ref}`, m.folderName);
  }
  return map;
}

/**
 * FRONT-776 THE TRAP: the upload dropzone must ignore an in-page file-card
 * drag — a real OS file drag always sets `'Files'` on `dataTransfer.types`
 * (readable during `dragover`, unlike `files`/`items`), our own card drag
 * (custom `FOLDER_DRAG_MIME`) never does. The dropzone's `onDragOver`/`onDrop`
 * literally call this before doing anything else, so the "must not light up
 * / must not attempt an upload" contract is provable by executing this
 * function, not just by reading the JSX.
 */
export function shouldDropzoneAcceptDrag(
  dataTransfer: { types?: readonly string[] | null } | null | undefined,
): boolean {
  return isDesktopFileDrag(dataTransfer?.types);
}

/**
 * FRONT-776 drag-to-move: turns a `fileItemKey`-shaped drag payload
 * (`upload:<id>` / `doc:<filename>`) into the `POST /api/folders/:id/files`
 * body — the single-item counterpart of `splitFileKeys`, which the bulk
 * "Move to folder…" action already uses for the same source/ref split.
 * `null` for a key matching neither prefix — unreachable in practice since
 * the key always comes from `fileItemKey`, but never silently mis-attaches.
 */
export function attachBodyForDragKey(key: string): { source: 'upload' | 'document'; ref: string } | null {
  const { fileIds, docNames } = splitFileKeys([key]);
  if (fileIds[0]) return { source: 'upload', ref: fileIds[0] };
  if (docNames[0]) return { source: 'document', ref: docNames[0] };
  return null;
}

/**
 * FRONT-776 code-review round 3, finding 11: the drag SOURCE — the feature's
 * entry point — previously had zero coverage (this repo's frontend tests run
 * `renderToStaticMarkup`, no jsdom, so a real drag can't be dispatched; see
 * `shouldDropzoneAcceptDrag`'s docblock above for the same constraint). Same
 * "pure gate, called directly by the JSX, never a parallel copy" discipline
 * as `shouldShowCreatePlanMenu`: never draggable on the recycle-bin tab
 * (nothing to move a trashed item INTO) or while folders don't exist as a
 * concept yet (flag off). Also never on 'bases'/'sketches' (FRONT-1549) —
 * neither renders a file card at all.
 */
export function isFileCardDraggable(tab: FileListTab, folderFlagEnabled: boolean): boolean {
  return tab === 'open' && folderFlagEnabled;
}

/** Minimal shape `startFileCardDrag` needs off `dataTransfer` — just enough
 *  to test against a plain object, no real `DataTransfer` required.
 *  `setDragImage` is what a real `DragEvent.dataTransfer` always has and a
 *  test stub can omit — `mountDragGhost` degrades gracefully without it. */
export interface FileDragTransferLike {
  effectAllowed?: string;
  setData: (type: string, data: string) => void;
  setDragImage?: DragImageTransferLike['setDragImage'];
}

/**
 * 2026-08-15 drag ghost polish: the `UnifiedFileItem` → `DragGhostSource`
 * mapping — kept HERE rather than in `~/lib/file-drag.ts` so that module
 * stays decoupled from this route's own item shape (see its own docblock).
 * Only `image` gets its own ghost glyph; every other upload kind and every
 * document collapses to the generic file glyph — the ghost's job is "which
 * kind of thing is this, roughly", not a pixel-perfect icon match.
 */
export function dragSourceForItem(item: UnifiedFileItem): DragGhostSource {
  return {
    key: fileItemKey(item),
    label: item.source === 'upload' ? item.name : item.title,
    kind: item.source === 'upload' && item.kind === 'image' ? 'image' : 'file',
  };
}

/**
 * FRONT-776 drag-to-move, extended 2026-08-15 for the multi-select carry
 * rule + the drag-ghost polish: the file card's drag SOURCE. Arms the native
 * drag with the `FOLDER_DRAG_MIME` payload, lifts the CARRIED keys
 * (`resolveDragCarryKeys` — the whole selection when the grabbed card is
 * part of it, otherwise just the grabbed card) into state so the folder-card
 * drop target (a sibling under the same `FileList` ancestor) can read them
 * without `dataTransfer.getData()` (see the module docblock), and mounts the
 * replacement drag-image ghost (`mountDragGhost`, injectable here so this
 * function's OWN wiring — which items end up in the ghost, in what order —
 * stays testable even though the DOM-touching mount itself can't be, in this
 * jsdom-less suite).
 *
 * Returns the ghost's cleanup — callers MUST invoke it on `dragend` (which
 * fires unconditionally: dropped on a folder, dropped nowhere, or
 * Escape-cancelled) or the offscreen ghost node leaks.
 */
export function startFileCardDrag(params: {
  item: UnifiedFileItem;
  visibleItems: readonly UnifiedFileItem[];
  selectedKeys: readonly string[];
  dataTransfer: FileDragTransferLike;
  setDraggedKeys: (keys: string[]) => void;
  setGrabbedKey: (key: string) => void;
  mountGhost?: (dataTransfer: DragImageTransferLike, model: DragGhostModel) => () => void;
}): () => void {
  const { item, visibleItems, selectedKeys, dataTransfer, setDraggedKeys, setGrabbedKey } = params;
  const grabbedKey = fileItemKey(item);
  const carriedKeys = resolveDragCarryKeys(grabbedKey, selectedKeys);

  const byKey = new Map(visibleItems.map((i) => [fileItemKey(i), i] as const));
  byKey.set(grabbedKey, item); // the grabbed item is always resolvable, even if visibleItems is stale
  const sources = carriedKeys.map((k) => byKey.get(k)).filter((i): i is UnifiedFileItem => Boolean(i)).map(dragSourceForItem);

  dataTransfer.effectAllowed = 'move';
  dataTransfer.setData(FOLDER_DRAG_MIME, JSON.stringify(carriedKeys));
  setGrabbedKey(grabbedKey);
  setDraggedKeys(carriedKeys);

  const model = buildDragGhostModel(sources);
  // An injected `mountGhost` (tests) always runs, even against a stub with
  // no `setDragImage` — it's the caller's own fake, it can do whatever it
  // wants with the args. The REAL `mountDragGhost` genuinely needs
  // `setDragImage` (every live `DragEvent.dataTransfer` has it); its absence
  // only happens against a bare synthetic event, where mounting a ghost
  // would throw for no benefit — no-op cleanup instead.
  if (params.mountGhost) return params.mountGhost(dataTransfer as DragImageTransferLike, model);
  if (!dataTransfer.setDragImage) return () => {};
  return mountDragGhost(dataTransfer as DragImageTransferLike, model);
}
